<template>
  <div id="app">
    <!-- YouTube player: hors-écran, audio seulement -->
    <div class="yt-offscreen" aria-hidden="true">
      <div id="youtube-player"></div>
    </div>

    <div class="scanlines" aria-hidden="true"></div>

    <main class="terminal">
      <!-- Header -->
      <header class="terminal-header">
        <span class="brand">HYPE NOT HYPE</span>
        <span class="live-badge" :class="{ playing: isPlaying }">
          <span class="live-dot"></span>{{ isPlaying ? 'ON AIR' : 'PAUSED' }}
        </span>
      </header>

      <!-- Track info -->
      <section class="now-playing">
        <div class="np-label">NOW PLAYING</div>
        <transition name="slide" mode="out-in">
          <div class="np-title" :key="track.title">{{ track.title || '---' }}</div>
        </transition>
        <transition name="slide" mode="out-in">
          <div class="np-artist" :key="track.artist">{{ track.artist || '---' }}</div>
        </transition>
      </section>

      <div class="rule" aria-hidden="true"></div>

      <!-- Controls -->
      <nav class="controls" :key="track.id">
        <button class="btn" :class="{ 'btn-active': isPlaying }" @click="playPause">
          {{ isPlaying ? '⏸ PAUSE' : '▶ PLAY' }}
        </button>
        <button class="btn" @click="skipTrack">⏭ SKIP</button>
        <button class="btn" :class="{ 'btn-muted': isMuted }" @click="toggleMute">
          {{ isMuted ? '✕ MUTED' : '♪ SOUND' }}
        </button>
        <button class="btn btn-hype" @click="vote(track.id, 'upvote')">HYPE ▲</button>
        <button class="btn btn-nope" @click="vote(track.id, 'downvote')">NOPE ▼</button>
      </nav>

      <div class="rule" aria-hidden="true"></div>

      <footer class="terminal-footer">
        <a href="#" @click.prevent="showAbout" class="about-link">ABOUT</a>
      </footer>
    </main>

    <!-- About Modal -->
    <div v-if="isAboutVisible" class="modal-backdrop" @click.self="hideAbout">
      <div class="modal-box" role="dialog">
        <div class="modal-title">// ABOUT</div>
        <p>HypeNotHype est une radio YouTube.<br>Sélection musicale par votes collectifs.</p>
        <button class="btn" @click="hideAbout">[ FERMER ]</button>
      </div>
    </div>
  </div>
</template>

<script>
const apiUrlPath = process.env.VUE_APP_API_URL;

export default {
  data() {
    return {
      isPlaying: false,
      isMuted: true,
      isAboutVisible: false,
      loading: false,
      error: null,
      player: null,
      playlist: [],
      track: {},
      currentTrackIndex: 0,
      online: navigator.onLine,
      youtubeApiReady: false,
    };
  },
  mounted() {
    this.initializeApplication();
  },
  beforeUnmount() {
    if (this.player) {
      this.player.destroy();
    }
    window.onYouTubeIframeAPIReady = null;
  },
  methods: {
    async initializeApplication() {
      this.loading = true;
      this.error = null;
      try {
        await this.initializeYouTubeAPI();
        await this.fetchPlaylist();
        this.createYouTubePlayer();
      } catch (error) {
        console.error("Initialization error:", error);
        this.error = "Failed to initialize the application. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    initializeYouTubeAPI() {
      return new Promise((resolve, reject) => {
        if (window.YT && window.YT.Player) {
          this.youtubeApiReady = true;
          resolve();
          return;
        }
        window.onYouTubeIframeAPIReady = () => {
          this.youtubeApiReady = true;
          resolve();
        };
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        tag.onerror = reject;
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      });
    },
    createYouTubePlayer() {
      this.player = new window.YT.Player("youtube-player", {
        width: "2",
        height: "2",
        videoId: "",
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          rel: 0,
          showinfo: 0,
          modestbranding: 1,
        },
        events: {
          onReady: this.onPlayerReady,
          onStateChange: this.onPlayerStateChange,
        },
      });
    },
    onPlayerReady() {
      this.player.mute();
      this.loadTrack(this.currentTrackIndex);
    },
    onPlayerStateChange(event) {
      if (event.data === window.YT.PlayerState.ENDED) {
        this.skipTrack();
      }
    },
    async fetchPlaylist() {
      const response = await fetch(apiUrlPath + "/fetch_playlist.php");
      this.playlist = await response.json();
    },
    loadTrack(index) {
      if (!this.online || !this.youtubeApiReady || index < 0 || index >= this.playlist.length) {
        return;
      }
      const { id, video_id, artist, title } = this.playlist[index];
      this.player.loadVideoById({ videoId: video_id, startSeconds: 1, suggestedQuality: "small" });
      this.track = { id, artist, title };
      this.player.playVideo();
      this.isPlaying = true;
    },
    async vote(trackId, voteType) {
      const response = await fetch(`${apiUrlPath}/vote.php?_=${Date.now()}`, {
        method: "POST",
        cache: "no-cache",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trackId, voteType }),
      });
      const result = await response.json();
      if (!result.success) {
        console.error("Vote failed:", result.error);
      }
    },
    playPause() {
      if (!this.online || !this.youtubeApiReady || !this.player) return;
      if (this.isPlaying) {
        this.player.pauseVideo();
      } else {
        this.player.playVideo();
      }
      this.isPlaying = !this.isPlaying;
    },
    skipTrack() {
      if (!this.online || !this.youtubeApiReady || !this.player) return;
      this.currentTrackIndex = (this.currentTrackIndex + 1) % this.playlist.length;
      this.loadTrack(this.currentTrackIndex);
    },
    toggleMute() {
      this.isMuted = !this.isMuted;
      if (this.isMuted) {
        this.player.mute();
      } else {
        this.player.unMute();
      }
    },
    showAbout() {
      this.isAboutVisible = true;
    },
    hideAbout() {
      this.isAboutVisible = false;
    },
  },
};
</script>

<style>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body, #app {
  background: #000;
  color: #fff;
  min-height: 100dvh;
  font-family: 'Share Tech Mono', 'Courier New', monospace;
}
</style>

<style scoped>
/* ── YouTube offscreen ───────────────────────────────────── */
.yt-offscreen {
  position: fixed;
  width: 2px;
  height: 2px;
  top: 0;
  left: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

/* ── Scanlines overlay ───────────────────────────────────── */
.scanlines {
  position: fixed;
  inset: 0;
  background: repeating-linear-gradient(
    0deg,
    transparent,
    transparent 3px,
    rgba(255, 255, 255, 0.025) 3px,
    rgba(255, 255, 255, 0.025) 4px
  );
  pointer-events: none;
  z-index: 100;
}

/* ── Main container ──────────────────────────────────────── */
#app {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  padding: 1rem;
}

.terminal {
  width: 100%;
  max-width: 480px;
  border: 1px solid #fff;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  position: relative;
}

/* Corner ticks */
.terminal::before,
.terminal::after {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  border-color: #fff;
  border-style: solid;
}

.terminal::before {
  top: -4px;
  right: -4px;
  border-width: 2px 2px 0 0;
}

.terminal::after {
  bottom: -4px;
  left: -4px;
  border-width: 0 0 2px 2px;
}

/* ── Header ──────────────────────────────────────────────── */
.terminal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'VT323', monospace;
  font-size: clamp(1.1rem, 4vw, 1.4rem);
  letter-spacing: 0.1em;
}

.brand {
  font-weight: normal;
}

.live-badge {
  display: flex;
  align-items: center;
  gap: 0.4em;
  font-size: 0.75em;
  opacity: 0.4;
  letter-spacing: 0.15em;
  transition: opacity 0.3s;
}

.live-badge.playing {
  opacity: 1;
}

.live-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fff;
  flex-shrink: 0;
}

.live-badge.playing .live-dot {
  animation: blink 1.2s step-start infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

/* ── Now playing ─────────────────────────────────────────── */
.now-playing {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-height: 6rem;
}

.np-label {
  font-size: 0.65rem;
  letter-spacing: 0.25em;
  opacity: 0.45;
}

.np-title {
  font-family: 'VT323', monospace;
  font-size: clamp(1.6rem, 6vw, 2.6rem);
  line-height: 1.1;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.np-artist {
  font-size: clamp(0.75rem, 2.5vw, 0.9rem);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.6;
}

/* ── Divider ─────────────────────────────────────────────── */
.rule {
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  width: 100%;
}

/* ── Controls ────────────────────────────────────────────── */
.controls {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.btn {
  font-family: 'Share Tech Mono', 'Courier New', monospace;
  font-size: clamp(0.65rem, 2vw, 0.75rem);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #fff;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.35);
  padding: 0.45em 0.9em;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  white-space: nowrap;
}

.btn:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: #fff;
}

.btn:active {
  background: #fff;
  color: #000;
}

.btn-active {
  border-color: #fff;
  background: rgba(255, 255, 255, 0.1);
}

.btn-muted {
  opacity: 0.4;
}

.btn-hype:hover { border-color: #fff; }
.btn-nope:hover { border-color: #fff; }

/* ── Footer ──────────────────────────────────────────────── */
.terminal-footer {
  display: flex;
  justify-content: flex-end;
}

.about-link {
  font-size: 0.6rem;
  letter-spacing: 0.2em;
  color: rgba(255, 255, 255, 0.3);
  text-decoration: none;
  transition: color 0.2s;
}

.about-link:hover {
  color: #fff;
}

/* ── Transitions ─────────────────────────────────────────── */
.slide-enter-active,
.slide-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

.slide-enter-to,
.slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}

/* ── Modal ───────────────────────────────────────────────── */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 1rem;
}

.modal-box {
  border: 1px solid #fff;
  padding: 1.5rem;
  max-width: 340px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-size: 0.85rem;
  line-height: 1.6;
}

.modal-title {
  font-family: 'VT323', monospace;
  font-size: 1.4rem;
  letter-spacing: 0.1em;
}
</style>
